import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useId, useRef, useState } from 'react';
import { t } from '@aquilixshop/i18n';
import { createPortal } from 'react-dom';
import { sanitizeRichtextHtml } from '../../sanitize/sanitizeRichtextHtml.js';
import { ALIGN_OPTIONS, marginFields, visibilityField } from '../_shared/blockFields.js';
import { marginStyle, num, str, visibilityCss } from '../_shared/blockRender.js';
import { useProductState } from '../_shared/productContext.js';
import { sizeRowCells } from '../size-row/index.js';
/**
 * Shrine `sizing-chart` (product buy-box block, preset "Sizing chart") — config-by-config.
 *
 * Section-specific shape: a TRIGGER (link/button) sitting in the product information column that, on
 * click, opens a MODAL popup holding a sizing TABLE of measurements, with an optional headline, caption
 * and top/bottom images. The modal open-state is client `useState`; SSR renders the trigger PLUS the
 * (hidden) modal markup so the table is present in the document and degrades gracefully — without JS the
 * fallback `<a href="#modal">` / `:target` is unused but the markup stays valid and the page still works.
 *
 * SHRINE INVENTORY (23 settings — every one implemented + wired, mapping our id ← Shrine id):
 *  - test_mode            ← test_mode (checkbox) — Shrine's preview flag (force the modal open while
 *                           styling). SUPERSEDED: in our builder, clicking the trigger on the canvas
 *                           opens the popup (document-capture listener below) — no flag needed.
 *  - button_label         ← button_label (inline_richtext) — the trigger text (sanitized inline html).
 *  - button_icon          ← button_icon — leading icon. Shrine uses a Material-Symbols GLYPH name; we map
 *                           the common size-guide glyphs ("straighten", "design_services", "checkroom",
 *                           "content_cut", "ruler") to small inline SVGs and render the matching one.
 *  - button_filled_icon   ← button_filled_icon — filled vs outline icon variant (swaps stroke→fill).
 *  - button_custom_icon   ← button_custom_icon (image) — overrides the glyph with an uploaded image.
 *  - text_size            ← text_size (range 12–22 px) — trigger font-size.
 *  - button_alignment     ← button_alignment (start/center/end) — trigger row justification.
 *  - button_underline     ← button_underline — underline the trigger label.
 *  - headline             ← headline — modal title (hidden when blank).
 *  - heading_size         ← heading_size (h4/h3/h2 → Small/Medium/Large) — title size.
 *  - close_button_style   ← close_button_style (outlined / minimalistic) — modal close-button chrome.
 *  - color_scheme         ← color_scheme — modal popup color scheme (mapped to --aqx-scheme-* vars).
 *  - table_header_content ← table_header_content (inline_richtext) — LEGACY read-only fallback: the
 *                           table (headers included) is now authored as `size-row` CHILD blocks, first
 *                           row = header band. Old docs' CSV headers still render until a row is added.
 *  - table_header_color_scheme ← table_header_color_scheme — header-row color scheme.
 *  - table_content        ← table_content (richtext) — LEGACY read-only fallback, same story: replaced
 *                           by child rows; old "line per row, commas between cells" docs still render.
 *  - caption_text         ← caption_text (richtext) — note under the table.
 *  - caption_size         ← caption_size (1.2/1.45/1.7rem → Small/Medium/Large).
 *  - caption_color        ← caption_color (color).
 *  - caption_alignment    ← caption_alignment (left/center/right).
 *  - top_image            ← top_image (image) — above the table.
 *  - bottom_image         ← bottom_image (image) — below the table + caption.
 *  - margin_top/bottom    ← margin_top / margin_bottom — marginFields.
 *
 * DEFERRED (justified — Shopify-platform coupling, the control itself is reproduced where one exists):
 *  - button_icon's Material-Symbols GLYPH FONT — we DON'T ship the Google Material-Symbols variable font
 *    (a separate sub-project); instead the icon NAME setting is honored against a small inline-SVG catalog
 *    of the size-guide glyphs Shrine ships by default, so the chosen icon renders faithfully without the font.
 *  - button_filled_icon's font-variation `FILL` axis — same font reason; emulated as outline→filled SVG swap.
 *  - color_scheme / table_header_color_scheme map to Shopify THEME color-scheme groups; we resolve them to
 *    our `--aqx-scheme-*` CSS vars (closest faithful equivalent) rather than Shopify scheme records.
 *  - button_custom_icon / top_image / bottom_image are Shopify CDN image_picker assets; we implement the
 *    image setting + render the chosen URL, but there is no Shopify `image_url`/srcset CDN transform.
 *  (No reveal/scroll ANIMATION, liquid-app hook, native share or cart-AJAX setting exists on this block.)
 */
export const sizingChartBlockSchema = {
    type: 'sizing-chart',
    name: 'Sizing chart',
    max: 1,
    // Table rows are CHILD `size-row` blocks (add / remove / reorder in the left tree, four plain cell
    // fields each) — replaces Shrine's "one row per line, commas between columns" richtext syntax,
    // which sellers should never have to type (builder-text-authoring rule). Legacy CSV settings from
    // already-saved docs still render via the Component's fallback.
    accepts: ['size-row'],
    fields: [
        visibilityField,
        // ── Open button ──────────────────────────────────────────────────────────────────────────────
        {
            id: 'buttonLabel',
            type: 'text',
            label: 'Button label',
            group: 'Open button',
            default: 'Guide des tailles',
            defaultKey: 'product.size_guide',
            maxLength: 80,
        },
        {
            // How the OPEN trigger renders: 'link' (the historical inline icon+label row) or 'card' (a
            // full-width surface card: optional image + bold title + a link-styled sub-label — the top-tier
            // PDP "How to choose the right size" size-guide card anatomy). The popup is identical in both.
            id: 'triggerStyle',
            type: 'select',
            label: 'Trigger style',
            group: 'Open button',
            default: 'link',
            options: [
                { value: 'link', label: 'Link (icon + label)' },
                { value: 'card', label: 'Card (image + title + link)' },
            ],
        },
        {
            id: 'triggerImage',
            type: 'image',
            label: 'Card image',
            group: 'Open button',
            info: 'Optional thumbnail shown on the left of the card.',
            visibleWhen: { field: 'triggerStyle', equals: 'card' },
        },
        {
            id: 'triggerLinkLabel',
            type: 'text',
            label: 'Card link label',
            group: 'Open button',
            default: 'Size guide',
            defaultKey: 'product.size_guide',
            maxLength: 60,
            visibleWhen: { field: 'triggerStyle', equals: 'card' },
        },
        {
            id: 'buttonIcon',
            type: 'select',
            label: 'Button icon',
            group: 'Open button',
            info: 'Leading icon shown before the label (rendered as an inline SVG).',
            default: 'straighten',
            visibleWhen: { field: 'triggerStyle', equals: 'link' },
            options: [
                { value: 'none', label: 'None' },
                { value: 'straighten', label: 'Ruler (straighten)' },
                { value: 'design_services', label: 'Design services' },
                { value: 'checkroom', label: 'Hanger (checkroom)' },
                { value: 'content_cut', label: 'Scissors' },
                { value: 'square_foot', label: 'Measure' },
            ],
        },
        {
            id: 'buttonFilledIcon',
            type: 'checkbox',
            label: 'Use filled icon',
            group: 'Open button',
            default: false,
            visibleWhen: { field: 'triggerStyle', equals: 'link' },
        },
        {
            id: 'buttonCustomIcon',
            type: 'image',
            label: 'Custom icon',
            group: 'Open button',
            info: 'Optional image that overrides the icon above.',
            visibleWhen: { field: 'triggerStyle', equals: 'link' },
        },
        {
            id: 'textSize',
            type: 'range',
            label: 'Text size',
            group: 'Open button',
            min: 12,
            max: 22,
            step: 1,
            default: 17,
            unit: 'px',
        },
        {
            id: 'buttonAlignment',
            type: 'select',
            label: 'Alignment',
            group: 'Open button',
            default: 'start',
            visibleWhen: { field: 'triggerStyle', equals: 'link' },
            options: [
                { value: 'start', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'end', label: 'Right' },
            ],
        },
        {
            id: 'buttonUnderline',
            type: 'checkbox',
            label: 'Underline label',
            group: 'Open button',
            default: false,
            visibleWhen: { field: 'triggerStyle', equals: 'link' },
        },
        // ── Popup ────────────────────────────────────────────────────────────────────────────────────
        {
            id: 'headline',
            type: 'text',
            label: 'Headline',
            group: 'Popup',
            default: 'Guide des tailles',
            defaultKey: 'product.size_guide',
            maxLength: 100,
        },
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: 'Popup',
            default: 'h3',
            options: [
                { value: 'h4', label: 'Small' },
                { value: 'h3', label: 'Medium' },
                { value: 'h2', label: 'Large' },
            ],
            visibleWhen: { field: 'headline', notEquals: '' },
        },
        {
            // Popup width: 'small' is the historical 560px table popup; 'large' is the wide reference-style
            // size-guide (diagram + side-by-side instructions).
            id: 'popupSize',
            type: 'select',
            label: 'Popup size',
            group: 'Popup',
            default: 'small',
            options: [
                { value: 'small', label: 'Small' },
                { value: 'large', label: 'Large' },
            ],
        },
        {
            id: 'closeButtonStyle',
            type: 'select',
            label: 'Close button style',
            group: 'Popup',
            default: 'minimalistic',
            options: [
                { value: 'outlined', label: 'Outlined' },
                { value: 'minimalistic', label: 'Minimalistic' },
            ],
        },
        {
            id: 'colorScheme',
            type: 'select',
            label: 'Color scheme',
            group: 'Popup',
            default: 'background-1',
            options: [
                { value: 'accent-1', label: 'Accent' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background' },
                { value: 'background-2', label: 'Surface' },
                { value: 'inverse', label: 'Inverse' },
            ],
        },
        // ── Table ────────────────────────────────────────────────────────────────────────────────────
        // The table itself is authored ENTIRELY as "Table row" child blocks — the first row is the
        // header band. No header/content fields here: everything the popup shows is visible in the tree.
        {
            // One-click OFF for the instructions-only popup (image + numbered steps, no measurements table).
            id: 'showTable',
            type: 'checkbox',
            label: 'Show table',
            group: 'Table',
            info: 'The table is built from "Table row" blocks added under this block in the tree.',
            default: true,
        },
        {
            id: 'firstRowHeader',
            type: 'checkbox',
            label: 'First row is the header',
            group: 'Table',
            info: 'The first "Table row" block renders as the table header band (styled by "Header color scheme").',
            default: true,
            visibleWhen: { field: 'showTable', equals: true },
        },
        {
            id: 'tableHeaderColorScheme',
            type: 'select',
            label: 'Header color scheme',
            group: 'Table',
            default: 'inverse',
            visibleWhen: { field: 'firstRowHeader', equals: true },
            options: [
                { value: 'accent-1', label: 'Accent' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background' },
                { value: 'background-2', label: 'Surface' },
                { value: 'inverse', label: 'Inverse' },
            ],
        },
        // ── Caption ──────────────────────────────────────────────────────────────────────────────────
        {
            id: 'captionText',
            type: 'richtext',
            label: 'Text',
            group: 'Text',
            info: 'Main popup text, under the image. Use two columns for side-by-side numbered steps.',
            default: {
                format: 'html',
                html: '<p>Aidez vos clients à choisir la bonne taille.</p>',
            },
        },
        {
            // '2' lays the caption paragraphs side-by-side on desktop — the reference "1. Measure your
            // head / 2. Find your fit" two-column instructions. Stacks back to one column on mobile.
            id: 'captionColumns',
            type: 'select',
            label: 'Columns (desktop)',
            group: 'Text',
            default: '1',
            options: [
                { value: '1', label: '1 column' },
                { value: '2', label: '2 columns' },
            ],
            visibleWhen: { field: 'captionText', notEquals: '' },
        },
        {
            id: 'captionSize',
            type: 'select',
            label: 'Text size',
            group: 'Text',
            default: '1.45rem',
            options: [
                { value: '1.2rem', label: 'Small' },
                { value: '1.45rem', label: 'Medium' },
                { value: '1.7rem', label: 'Large' },
            ],
            visibleWhen: { field: 'captionText', notEquals: '' },
        },
        {
            id: 'captionColor',
            type: 'color',
            label: 'Text color',
            group: 'Text',
            default: { ref: 'custom', value: '#121212' },
            visibleWhen: { field: 'captionText', notEquals: '' },
        },
        {
            id: 'captionAlignment',
            type: 'select',
            label: 'Text alignment',
            group: 'Text',
            default: 'left',
            options: ALIGN_OPTIONS,
            visibleWhen: { field: 'captionText', notEquals: '' },
        },
        // ── Images ───────────────────────────────────────────────────────────────────────────────────
        {
            id: 'topImage',
            type: 'image',
            label: 'Top image',
            group: 'Images',
            info: 'Shown above the table.',
        },
        {
            id: 'bottomImage',
            type: 'image',
            label: 'Bottom image',
            group: 'Images',
            info: 'Shown below the table and caption.',
        },
        // ── Margins ──────────────────────────────────────────────────────────────────────────────────
        ...marginFields(15, 15),
    ],
};
/**
 * Map our color-scheme select value to the matching `--aqx-scheme-*` background/text pair.
 * Literal fallbacks matter: the popup is PORTALLED to <body>, outside any `data-scheme` ancestor,
 * so the vars may be unset there — without fallbacks the dialog painted transparent.
 */
function schemePair(value) {
    switch (value) {
        case 'accent-1':
        case 'accent-2':
            return {
                bg: 'var(--aqx-scheme-accent, #121212)',
                text: 'var(--aqx-scheme-accent-contrast, #ffffff)',
            };
        case 'background-2':
            return { bg: 'var(--aqx-scheme-surface, #f6f6f6)', text: 'var(--aqx-scheme-text, #121212)' };
        case 'inverse':
            return { bg: 'var(--aqx-scheme-text, #121212)', text: 'var(--aqx-scheme-bg, #ffffff)' };
        case 'background-1':
        default:
            return { bg: 'var(--aqx-scheme-bg, #ffffff)', text: 'var(--aqx-scheme-text, #121212)' };
    }
}
/** A stored `color` setting is a ColorValue; read its hex (custom) or fall back to a literal. */
function colorHex(v, fallback) {
    if (v && typeof v === 'object') {
        const c = v;
        if (c.ref === 'custom' && typeof c.value === 'string')
            return c.value;
    }
    if (typeof v === 'string' && v.length > 0)
        return v;
    return fallback;
}
/** Resolve an image setting to its URL (or '' when unset). */
function imageUrl(v) {
    if (v && typeof v === 'object') {
        const img = v;
        if (typeof img.url === 'string')
            return img.url;
        if (typeof img.src === 'string')
            return img.src;
    }
    return '';
}
/** Heading size select → CSS font-size. */
const HEADING_PX = { h4: '18px', h3: '22px', h2: '28px' };
const JUSTIFY = {
    start: 'flex-start',
    center: 'center',
    end: 'flex-end',
    left: 'flex-start',
    right: 'flex-end',
};
const TEXT_ALIGN = {
    left: 'left',
    center: 'center',
    right: 'right',
};
/**
 * Inline-SVG catalog for the size-guide glyphs Shrine ships. `filled` swaps the outline (stroke) variant
 * for a solid (fill) one where it reads better. Returns null for "none"/unknown.
 */
function buttonIconSvg(name, filled) {
    const stroke = {
        stroke: 'currentColor',
        strokeWidth: 1.7,
        fill: 'none',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
    };
    switch (name) {
        case 'straighten':
            // A ruler.
            return (_jsx("svg", { width: "1em", height: "1em", viewBox: "0 0 24 24", "aria-hidden": "true", children: filled ? (_jsxs(_Fragment, { children: [_jsx("rect", { x: "2", y: "8", width: "20", height: "8", rx: "1.5", fill: "currentColor" }), _jsx("path", { d: "M6 8v3M10 8v4M14 8v3M18 8v4", stroke: "var(--aqx-scheme-bg, #fff)", strokeWidth: "1.4" })] })) : (_jsxs(_Fragment, { children: [_jsx("rect", { x: "2.5", y: "8.5", width: "19", height: "7", rx: "1.5", ...stroke }), _jsx("path", { d: "M6 8.5v3M10 8.5v4M14 8.5v3M18 8.5v4", ...stroke })] })) }));
        case 'design_services':
            // A drafting triangle / pencil.
            return (_jsxs("svg", { width: "1em", height: "1em", viewBox: "0 0 24 24", "aria-hidden": "true", children: [_jsx("path", { d: "M3 20h18M5 20 19 6l-1-1L4 19z", ...stroke, fill: filled ? 'currentColor' : 'none' }), _jsx("path", { d: "M14 8l2 2", ...stroke })] }));
        case 'checkroom':
            // A clothes hanger.
            return (_jsx("svg", { width: "1em", height: "1em", viewBox: "0 0 24 24", "aria-hidden": "true", children: _jsx("path", { d: "M12 7a2 2 0 1 1 1 1.7L3 16h18L13 8.7", ...stroke, fill: filled ? 'currentColor' : 'none' }) }));
        case 'content_cut':
            // Scissors.
            return (_jsxs("svg", { width: "1em", height: "1em", viewBox: "0 0 24 24", "aria-hidden": "true", children: [_jsx("circle", { cx: "6", cy: "6", r: "2.5", ...stroke, fill: filled ? 'currentColor' : 'none' }), _jsx("circle", { cx: "6", cy: "18", r: "2.5", ...stroke, fill: filled ? 'currentColor' : 'none' }), _jsx("path", { d: "M8 7.5 20 18M8 16.5 20 6", ...stroke })] }));
        case 'square_foot':
            // A measure / set-square.
            return (_jsxs("svg", { width: "1em", height: "1em", viewBox: "0 0 24 24", "aria-hidden": "true", children: [_jsx("path", { d: "M4 4v16h16L4 4z", ...stroke, fill: filled ? 'currentColor' : 'none' }), _jsx("path", { d: "M4 9h3M4 13h3M4 17h3", ...stroke })] }));
        default:
            return null;
    }
}
/** Split a comma-separated header line into cells; "[empty]" → blank cell. */
function splitCells(raw) {
    return raw.split(',').map((c) => {
        const t = c.trim();
        return t.toLowerCase() === '[empty]' ? '' : t;
    });
}
/**
 * Parse the `table_content` richtext into rows of cells. Each top-level `<p>` is one row; commas split
 * columns; an "[empty]" token = a blank cell. We strip tags from each `<p>` to get the row text, so the
 * author writes plain "S, 95cm, 61cm" lines (Shrine's exact authoring model).
 */
function parseRows(html) {
    const clean = sanitizeRichtextHtml(html);
    if (!clean)
        return [];
    // Split on paragraph / line-break boundaries, then strip remaining tags per line.
    const lines = clean
        .replace(/<\/(p|div|li)>/gi, '\n')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .split('\n')
        .map((l) => l.replace(/&nbsp;/g, ' ').trim())
        .filter(Boolean);
    return lines.map(splitCells);
}
/**
 * Sizing-chart: a trigger that opens a modal popup with a measurements table, optional headline, caption
 * and top/bottom images. Product buy-box block — renders nothing outside a product container.
 */
const SizingChartBlock = ({ block, context }) => {
    const ps = useProductState();
    const bs = block.settings;
    const cls = `aqx-szc-${block.id}`;
    const titleId = useId();
    const isEditing = context?.renderMode === 'edit';
    const [open, setOpen] = useState(false);
    const triggerRef = useRef(null);
    // BUILDER: once the section is selected, the canvas intercepts clicks in the CAPTURE phase to
    // drill-in-select the block (stopPropagation → the trigger's React onClick never fires, so "Size
    // guide" seemed dead in the editor). A native DOCUMENT-level capture listener runs BEFORE the
    // canvas handler, so clicking the trigger both selects the block AND previews the popup —
    // Shopify's editor behaviour. Live storefront keeps the plain onClick.
    useEffect(() => {
        if (!isEditing || typeof document === 'undefined')
            return;
        const onDocClick = (e) => {
            const el = triggerRef.current;
            if (el && e.target instanceof Node && el.contains(e.target))
                setOpen(true);
        };
        document.addEventListener('click', onDocClick, true);
        return () => document.removeEventListener('click', onDocClick, true);
    }, [isEditing]);
    // Lock body scroll while the modal is open (client-only, SSR-safe via effect).
    useEffect(() => {
        if (!open || typeof document === 'undefined')
            return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);
    // Close on Escape.
    useEffect(() => {
        if (!open || typeof window === 'undefined')
            return;
        const onKey = (e) => {
            if (e.key === 'Escape')
                setOpen(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open]);
    if (!ps)
        return null;
    // ── Open button ──────────────────────────────────────────────────────────────────────────────
    const labelHtml = sanitizeRichtextHtml(`<span>${str(bs.buttonLabel, 'Sizing chart')}</span>`);
    const textSize = num(bs.textSize, 17);
    const buttonAlign = str(bs.buttonAlignment, 'start');
    const underline = bs.buttonUnderline === true;
    const customIconUrl = imageUrl(bs.buttonCustomIcon);
    const iconName = str(bs.buttonIcon, 'straighten');
    const filledIcon = bs.buttonFilledIcon === true;
    // Trigger style: 'card' renders the full-width size-guide card (image + title + link sub-label);
    // 'link' (default) keeps the historical inline icon+label trigger untouched.
    const cardTrigger = str(bs.triggerStyle, 'link') === 'card';
    const cardImgUrl = imageUrl(bs.triggerImage);
    const cardLinkLabel = str(bs.triggerLinkLabel, 'Size guide');
    // ── Popup ────────────────────────────────────────────────────────────────────────────────────
    const headline = str(bs.headline, 'Guide des tailles');
    const headingPx = HEADING_PX[str(bs.headingSize, 'h3')] ?? '22px';
    const popupWide = str(bs.popupSize, 'small') === 'large';
    const closeMinimal = str(bs.closeButtonStyle, 'minimalistic') === 'minimalistic';
    const scheme = schemePair(str(bs.colorScheme, 'background-1'));
    const headerScheme = schemePair(str(bs.tableHeaderColorScheme, 'inverse'));
    // ── Table ────────────────────────────────────────────────────────────────────────────────────
    const showTable = bs.showTable !== false;
    const firstRowHeader = bs.firstRowHeader !== false;
    // The whole table is `size-row` CHILD blocks; the FIRST child is the header band (toggleable).
    // The header/body split works on the CHILD LIST, not the rendered rows: a disabled or still-empty
    // first row means "no header band", it must never silently promote a data row into <thead>.
    const rowChildren = (block.blocks ?? []).filter((b) => b.type === 'size-row');
    const cellsOf = (b) => sizeRowCells(b.settings);
    let headers;
    let rows;
    if (rowChildren.length > 0) {
        const headSlot = firstRowHeader ? rowChildren[0] : undefined;
        headers = headSlot && !headSlot.disabled ? cellsOf(headSlot) : [];
        rows = rowChildren
            .filter((b) => b !== headSlot && !b.disabled)
            .map(cellsOf)
            .filter((cells) => cells.length > 0);
    }
    else {
        // LEGACY docs saved before the child-row model: plain `header1..4` settings or the Shrine CSV
        // header line, plus the "line per row, commas between cells" richtext. Normally dead code — the
        // hydrate migration converts these into size-row children — kept as a safety net for render
        // paths that skip hydration. The moment ONE size-row child exists (even disabled/empty), the
        // legacy settings are ignored entirely, so hiding your rows can never resurrect a ghost table.
        const headerFields = sizeRowCells({
            c1: bs.header1,
            c2: bs.header2,
            c3: bs.header3,
            c4: bs.header4,
        });
        const legacyHeaderCsv = str(bs.tableHeaderContent, '').trim();
        headers =
            headerFields.length > 0 ? headerFields : legacyHeaderCsv ? splitCells(legacyHeaderCsv) : [];
        const tableRt = bs.tableContent;
        rows = parseRows(tableRt?.format === 'html' ? tableRt.html : '');
    }
    const hasHeaders = headers.some((h) => h.length > 0);
    // Builder-only empty state: a fresh block has no rows at all — the popup must SAY how to build the
    // table instead of silently showing nothing (the old demo-table defaults are gone).
    const showTableHint = isEditing && showTable && rowChildren.length === 0 && rows.length === 0 && !hasHeaders;
    // The table's real column count is the WIDEST of headers and rows; pad the shorter ones with empty
    // cells so the header band always spans the full table (2 headers + a 4-cell row used to leave the
    // last columns header-less, visually cutting the header row short).
    const colCount = Math.max(headers.length, ...rows.map((r) => r.length), 1);
    const pad = (cells) => cells.length >= colCount ? cells : [...cells, ...Array(colCount - cells.length).fill('')];
    // ── Caption ──────────────────────────────────────────────────────────────────────────────────
    const captionRt = bs.captionText;
    const captionHtml = sanitizeRichtextHtml(captionRt?.format === 'html' ? captionRt.html : '');
    const captionSize = str(bs.captionSize, '1.45rem');
    const captionColor = colorHex(bs.captionColor, '#121212');
    const captionAlign = str(bs.captionAlignment, 'left');
    const captionCols2 = str(bs.captionColumns, '1') === '2';
    // ── Images ───────────────────────────────────────────────────────────────────────────────────
    const topImg = imageUrl(bs.topImage);
    const bottomImg = imageUrl(bs.bottomImage);
    const css = `.${cls}{display:flex;justify-content:${JUSTIFY[buttonAlign] ?? 'flex-start'}}` +
        `.${cls}__trigger{display:inline-flex;align-items:center;gap:6px;background:none;border:0;padding:0;` +
        `cursor:pointer;font-family:var(--aqx-theme-font);font-size:${textSize}px;line-height:1.3;` +
        `color:var(--aqx-scheme-text);${underline ? 'text-decoration:underline;text-underline-offset:3px;' : ''}}` +
        `.${cls}__trigger:hover{opacity:.75}` +
        `.${cls}__trigger img{width:1.1em;height:1.1em;object-fit:contain;display:block}` +
        `.${cls}__trigger p{margin:0;display:inline}` +
        // Card trigger: full-width surface card (image + stacked title/link), overrides the inline look.
        `.${cls}__trigger--card{width:100%;justify-content:flex-start;gap:16px;text-align:start;` +
        `background:var(--aqx-scheme-surface, rgba(0,0,0,.04));border-radius:var(--aqx-theme-radius-md, 10px);` +
        `padding:16px 20px}` +
        `.${cls}__trigger--card:hover{opacity:1;filter:brightness(.98)}` +
        `.${cls}__trigger--card img{width:72px;height:72px;object-fit:contain}` +
        `.${cls}__cardbody{display:flex;flex-direction:column;gap:4px;align-items:flex-start}` +
        `.${cls}__cardtitle{font-weight:600}` +
        `.${cls}__cardlink{font-size:.85em;color:var(--aqx-scheme-text-subdued, color-mix(in srgb, var(--aqx-scheme-text) 60%, transparent))}` +
        // Overlay + dialog
        `.${cls}__overlay{position:fixed;inset:0;z-index:1000;display:flex;align-items:center;` +
        `justify-content:center;padding:20px;background:rgba(0,0,0,.5)}` +
        `.${cls}__dialog{position:relative;width:100%;max-width:${popupWide ? '940px' : '560px'};max-height:85vh;overflow:auto;` +
        `border-radius:12px;padding:28px;box-shadow:0 20px 60px rgba(0,0,0,.3);` +
        `background:${scheme.bg};color:${scheme.text}}` +
        `.${cls}__title{margin:0 36px 16px 0;font-family:var(--aqx-theme-heading-font, var(--aqx-theme-font));` +
        `font-weight:600;font-size:${headingPx};color:${scheme.text}}` +
        `.${cls}__close{position:absolute;top:16px;right:16px;width:32px;height:32px;display:inline-flex;` +
        `align-items:center;justify-content:center;cursor:pointer;color:${scheme.text};background:none;` +
        (closeMinimal ? 'border:0;' : 'border:1px solid currentColor;border-radius:50%;') +
        `}` +
        `.${cls}__close:hover{opacity:.7}` +
        // Table
        // The popup is PORTALLED to <body>, and the storefront's `body{overflow-wrap:anywhere}` reset
        // (global.css — a long word must not push the page sideways) inherits straight into these cells.
        // On a phone that reset was shredding the measurements: the narrow columns broke INSIDE their
        // words — "UK" stacked as U/K, "10.5" as "10."/"5" — while the one column with a long header
        // ("Longueur du pied") kept all the leftover width, so the table read as lopsided and unreadable.
        // Cells therefore opt OUT of the reset (`normal` + no hyphens), data cells never wrap at all, and
        // the table sits in a horizontally scrollable wrapper so a table wider than the dialog scrolls
        // instead of being squeezed below its own min-content width.
        `.${cls}__tablewrap{max-width:100%;overflow-x:auto;-webkit-overflow-scrolling:touch;overscroll-behavior-x:contain}` +
        `.${cls}__table{width:100%;border-collapse:collapse;font-size:14px}` +
        // Equal column shares (`width: 100/n %`, auto layout): without them the browser hands the leftover
        // width out in PROPORTION to content, so one long header ("Longueur du pied") swallowed the row
        // while "UK" got a sliver. A percentage is a PREFERENCE in auto layout — a column whose content
        // needs more still takes more — so nothing ever overflows; the columns just start out even.
        `.${cls}__table th,.${cls}__table td{width:${(100 / Math.max(1, colCount)).toFixed(3)}%;padding:8px 10px;` +
        `border:1px solid var(--aqx-scheme-border, rgba(0,0,0,.12));` +
        `text-align:start;word-break:normal;overflow-wrap:normal;hyphens:none}` +
        `.${cls}__table td{white-space:nowrap}` +
        `.${cls}__table thead th{background:${headerScheme.bg};color:${headerScheme.text};font-weight:600}` +
        `.${cls}__tablehint{border:1px dashed var(--aqx-scheme-border, rgba(0,0,0,.25));border-radius:8px;` +
        `padding:14px 16px;font-size:13px;opacity:.7;text-align:center}` +
        `.${cls}__img{width:100%;height:auto;display:block;border-radius:var(--aqx-theme-radius-media,0px);margin:0 0 16px}` +
        `.${cls}__img--bottom{margin:16px 0 0}` +
        `.${cls}__caption{margin-top:16px;font-size:${captionSize};color:${captionColor};text-align:${TEXT_ALIGN[captionAlign] ?? 'left'}}` +
        `.${cls}__caption p{margin:0 0 6px}` +
        // Popup-internal responsive rules use @media, NOT @container aqx-page: the dialog is PORTALLED
        // to <body>, OUTSIDE the page container, so a container query can never match it — and since the
        // portal overlays the REAL viewport (not the builder's device frame), the viewport is exactly
        // the right thing to key off.
        // Phone: the 20px overlay gutter + 28px dialog padding ate 96px of a 320px screen before the
        // table got a single pixel. Tighten both, and the close button with them.
        `@media (max-width:749px){.${cls}__overlay{padding:12px}` +
        `.${cls}__dialog{padding:20px 16px 24px}` +
        `.${cls}__title{margin-right:32px}` +
        `.${cls}__close{top:10px;right:10px}` +
        `.${cls}__table th,.${cls}__table td{padding:7px 8px}}` +
        `@media (min-width:750px){.${cls}__table{font-size:15px}` +
        // Two-column caption (reference "1. Measure / 2. Find your fit" side-by-side) — desktop only,
        // each paragraph keeps its own column; stacks back to a single column on mobile.
        (captionCols2
            ? `.${cls}__caption{column-count:2;column-gap:36px}.${cls}__caption p{break-inside:avoid}`
            : '') +
        `}` +
        visibilityCss(cls, bs.visibility);
    const triggerInner = cardTrigger ? (_jsxs(_Fragment, { children: [cardImgUrl ? _jsx("img", { src: cardImgUrl, alt: "", "aria-hidden": "true" }) : null, _jsxs("span", { className: `${cls}__cardbody`, children: [_jsx("span", { className: `${cls}__cardtitle`, dangerouslySetInnerHTML: { __html: labelHtml } }), cardLinkLabel ? _jsxs("span", { className: `${cls}__cardlink`, children: [cardLinkLabel, " \u203A"] }) : null] })] })) : (_jsxs(_Fragment, { children: [customIconUrl ? (_jsx("img", { src: customIconUrl, alt: "", "aria-hidden": "true" })) : (buttonIconSvg(iconName, filledIcon)), _jsx("span", { className: `${cls}__labeltext`, dangerouslySetInnerHTML: { __html: labelHtml } })] }));
    return (_jsxs(_Fragment, { children: [_jsx("style", { dangerouslySetInnerHTML: { __html: css } }), _jsx("div", { className: cls, style: { ...marginStyle(bs, 15, 15) }, children: _jsx("button", { ref: triggerRef, type: "button", className: `${cls}__trigger${cardTrigger ? ` ${cls}__trigger--card` : ''}`, "aria-haspopup": "dialog", "aria-expanded": open, onClick: () => setOpen(true), children: triggerInner }) }), open && typeof document !== 'undefined'
                ? createPortal(_jsx("div", { className: `${cls}__overlay`, onClick: (e) => {
                        if (e.target === e.currentTarget)
                            setOpen(false);
                    }, children: _jsxs("div", { className: `${cls}__dialog`, role: "dialog", "aria-modal": "true", "aria-labelledby": headline ? titleId : undefined, children: [_jsx("button", { type: "button", className: `${cls}__close`, "aria-label": t(context, 'a11y.close'), onClick: () => setOpen(false), children: _jsx("svg", { width: "16", height: "16", viewBox: "0 0 16 16", "aria-hidden": "true", children: _jsx("path", { d: "M2 2l12 12M14 2 2 14", stroke: "currentColor", strokeWidth: "1.8", strokeLinecap: "round" }) }) }), headline ? (_jsx("div", { id: titleId, className: `${cls}__title`, children: headline })) : null, topImg ? _jsx("img", { className: `${cls}__img`, src: topImg, alt: "" }) : null, showTable && (rows.length > 0 || hasHeaders) ? (_jsx("div", { className: `${cls}__tablewrap`, children: _jsxs("table", { className: `${cls}__table`, children: [hasHeaders ? (_jsx("thead", { children: _jsx("tr", { children: pad(headers).map((h, i) => (_jsx("th", { scope: "col", children: h }, i))) }) })) : null, _jsx("tbody", { children: rows.map((cells, r) => (_jsx("tr", { children: pad(cells).map((cell, cidx) => (_jsx("td", { children: cell }, cidx))) }, r))) })] }) })) : showTableHint ? (_jsx("div", { className: `${cls}__tablehint`, children: t(context, 'builder.sizing_table_hint') })) : null, captionHtml ? (_jsx("div", { className: `${cls}__caption`, dangerouslySetInnerHTML: { __html: captionHtml } })) : null, bottomImg ? (_jsx("img", { className: `${cls}__img ${cls}__img--bottom`, src: bottomImg, alt: "" })) : null] }) }), document.body)
                : null] }));
};
export const sizingChartBlock = {
    type: 'sizing-chart',
    schema: sizingChartBlockSchema,
    Component: SizingChartBlock,
    meta: { label: 'Sizing chart' },
};
//# sourceMappingURL=index.js.map